import React, { Dispatch, SetStateAction, useState } from 'react';

import { useMutation, useQueryClient } from 'react-query';
import { useNavigate, useParams } from 'react-router-dom';
import Swal from 'sweetalert2';

import {
  IConsultantData,
  IConsultantSubAndBillingId,
} from 'src/environments/CheckoutConsultantAPP/interfaces';
import { apiCheckoutConsultant } from 'src/environments/CheckoutConsultantAPP/api';

import { ICardIdData, IPlanData } from 'src/environments/Checkout/interfaces';

import { BackArrowIcon } from 'src/components/style/BackArrowIcon';

import {
  Button,
  Card,
  CardInfo,
  ContainerCards,
  LoadingGif,
  IconContainer,
  TrashIcon,
  CreditCardIcon,
  CheckIcon,
} from './style';

import creditCardIconSVG from '../../../../../../../../assets/images/subscripiton-checkout-icons/black-credit-card-icon.svg';
import whiteArrowIconSVG from '../../../../../../../../assets/images/subscripiton-checkout-icons/white-arrow-left.svg';
import trashIconSVG from '../../../../../../../../assets/images/subscripiton-checkout-icons/trash-icon-white.svg';
import checkIconSVG from '../../../../../../../../assets/images/subscripiton-checkout-icons/check-icon.svg';
import loadingGIF from '../../../../../../../../assets/gifs/loading.png';

type Props = {
  consultantData: IConsultantData;
  cardsIds: ICardIdData[];
  planData: IPlanData;
  setShowCardForm: Dispatch<SetStateAction<boolean>>;
  setComponent?: Dispatch<SetStateAction<number>>;
  platform: string;
};

export default function ConcludePurchase({
  consultantData,
  cardsIds,
  planData,
  setShowCardForm,
  setComponent,
  platform,
}: Props): JSX.Element {
  const [loading, setLoading] = useState(false);
  const [disabledTrash, setDisabledTrash] = useState(false);
  const [selectedCardId, setSelectedCardId] = useState(0);
  const [disabledOtherCard, setDisabledOtherCard] = useState(false);

  const queryClient = useQueryClient();

  const navigate = useNavigate();

  const { token } = useParams();

  const { mutate: deleteCardIdMutation } = useMutation(
    async (billingId: number) => apiCheckoutConsultant.deleteConsultantCardId(billingId, token),
    {
      onError: () => {
        void Swal.fire({
          icon: 'error',
          title: 'Ocorreu um erro!',
          text: 'Por favor, tente novamente mais tarde, ou contate a central.',
          iconColor: 'black',
          confirmButtonColor: 'black',
        });
        setDisabledTrash(false);
        setLoading(false);
        setDisabledOtherCard(false);
      },
      onSuccess: async () => {
        setSelectedCardId(0);
        await queryClient.invalidateQueries('getConsultantCardsIds');
        setDisabledTrash(false);
        setLoading(false);
        setDisabledOtherCard(false);
        void Swal.fire({
          position: 'top-end',
          icon: 'success',
          title: 'Cartão removido com sucesso!',
          showConfirmButton: false,
          timer: 2000,
        });
      },
    }
  );

  const { mutate: consultantSubscriptionMutate } = useMutation(
    async ({ subscriptionData, billingId }: IConsultantSubAndBillingId) =>
      apiCheckoutConsultant.createConsultantSubscriptionPlan(subscriptionData, billingId, token),
    {
      onError: () => {
        void Swal.fire({
          icon: 'error',
          title: 'Pagamento não autorizado!',
          text: 'Por favor, tente novamente mais tarde, ou contate a central.',
          iconColor: 'black',
          confirmButtonColor: 'black',
        });
        setDisabledTrash(false);
        setLoading(false);
        setDisabledOtherCard(false);
      },
      onSuccess: async () => {
        navigate(`/pagamento-concluido-plano-consultora/${platform}`);
      },
    }
  );

  const handleRemoveCard = (billingId: number): void => {
    setDisabledTrash(true);
    setLoading(true);
    setDisabledOtherCard(true);
    deleteCardIdMutation(billingId);
  };

  const handleCheckout = (): void => {
    if (selectedCardId === 0) {
      void Swal.fire({
        icon: 'warning',
        title: 'Selecione um cartão para finalizar a compra!',
        iconColor: 'black',
        confirmButtonColor: 'black',
      });
      return;
    }

    setDisabledTrash(true);
    setLoading(true);
    setDisabledOtherCard(true);

    const subscriptionData = {
      ...consultantData,
      consultantPhone: consultantData.consultantPhone.replace(/[^a-zA-Z0-9]/g, '').replace(' ', ''),
      consultantCpf: consultantData.consultantCpf.replace(/[^a-zA-Z0-9]/g, ''),
      zipCode: consultantData.zipCode.replace('-', ''),
      planName: planData.planName,
      planValue: planData.price / 100,
      planId: planData.planId,
      platform,
    };

    consultantSubscriptionMutate({ subscriptionData, billingId: selectedCardId });
  };

  return (
    <>
      <ContainerCards>
        <BackArrowIcon
          src={whiteArrowIconSVG}
          onClick={() => {
            setComponent((prev: number) => prev - 1);
          }}
        />
        <h1>Escolha um cartão ou adicione um novo</h1>
        {cardsIds.map((billing, index) => (
          <Card
            key={index}
            role="button"
            border={selectedCardId !== billing.id ? '1px solid #cccccc' : '1px solid black'}
            onClick={() => {
              if (selectedCardId === billing.id) {
                setSelectedCardId(0);
                return;
              }
              setSelectedCardId(billing.id);
            }}
          >
            <CreditCardIcon src={creditCardIconSVG} alt="Ícone de cartão de crédito" />
            <CardInfo>
              <p>{billing.brand}</p>
              <p>**** **** **** {billing.lastFourDigits}</p>
            </CardInfo>
            <IconContainer
              onClick={() => {
                if (selectedCardId === billing.id) return;
                handleRemoveCard(billing.id);
              }}
              disabled={disabledTrash}
            >
              {selectedCardId === billing.id ? (
                <CheckIcon
                  src={checkIconSVG}
                  alt="Ícone de check mostranso que o cartão está selecionado"
                />
              ) : (
                <TrashIcon src={trashIconSVG} alt="Icone de lixeira para deletar o cartão" />
              )}
            </IconContainer>
          </Card>
        ))}
      </ContainerCards>
      <Button type="button" disabled={loading} onClick={handleCheckout}>
        {loading ? <LoadingGif src={loadingGIF} alt="gif de carregamento" /> : 'FINALIZAR'}
      </Button>
      <Button
        type="button"
        disabled={disabledOtherCard}
        onClick={() => {
          setShowCardForm(true);
        }}
      >
        ADICIONAR NOVO CARTÃO
      </Button>
    </>
  );
}
